import { useMemo, useState } from "react";
import MaterialTable from "material-table";
import type { Column } from "material-table";
import { Button, TextField, Tooltip } from "@material-ui/core";
import AddIcon from "@material-ui/icons/Add";
import VisibilityIcon from "@material-ui/icons/Visibility";
import EditIcon from "@material-ui/icons/Edit";
import CheckCircleIcon from "@material-ui/icons/CheckCircle";
import CancelIcon from "@material-ui/icons/Cancel";
import DeleteIcon from "@material-ui/icons/Delete";
import HistoryIcon from "@material-ui/icons/History";
import LocalShippingIcon from "@material-ui/icons/LocalShipping";
import {
  useGetDispatches,
  useGetDispatch,
  useCreateDispatch,
  useUpdateDispatch,
  useApproveDispatch,
  useRejectDispatch,
  useDeleteDispatch,
  useAddDispatchGuide,
} from "../../utils/react-query/queryDispatches/queries";
import {
  useMyPermissions,
  useMyUserId,
} from "../../utils/react-query/queryRbac/queries";
import { successToast, errorToast } from "../../services/toasts";
import type {
  ApproveDispatchPayload,
  RejectDispatchPayload,
} from "../../services";
import { DispatchForm } from "./components/DispatchForm";
import { DispatchDetail } from "./components/DispatchDetail";
import { DispatchHistoryModal } from "./components/DispatchHistoryModal";
import { DispatchValidationDialog } from "./components/DispatchValidationDialog";
import type { DispatchValidationSubmit } from "./components/DispatchValidationDialog";
import { Sheet, SheetContent } from "../../components/Sheet";
import {
  RowActionsMenu,
  RowActionItem,
} from "../../components/RowActionsMenu/RowActionsMenu";
import styles from "./DispatchesPage.module.scss";
import {
  statusLabels,
  statusBadgeClass,
  validationLabels,
  validationBadgeClass,
  isEditableStatus,
} from "./statusLabels";
import { methodLabels } from "./methods";
import type {
  CreateDispatchPayload,
  UpdateDispatchPayload,
  Dispatch,
  DispatchMethod,
  DispatchStatus,
} from "./types";

const materialTableES = {
  header: { actions: "" },
  body: {
    emptyDataSourceMessage: "No se encontraron despachos",
    deleteTooltip: "Eliminar",
    editTooltip: "Editar",
    editRow: {
      deleteText: "¿Estas seguro que deseas eliminar este registro?",
      saveTooltip: "Guardar",
      cancelTooltip: "Cancelar",
    },
    filterRow: { filterTooltip: "Filtrar" },
  },
  pagination: {
    labelRowsSelect: "Filas",
    labelRowsPerPage: "Filas por página",
    firstAriaLabel: "Primera página",
    firstTooltip: "Primera página",
    previousAriaLabel: "Página anterior",
    previousTooltip: "Página anterior",
    nextAriaLabel: "Siguiente página",
    nextTooltip: "Siguiente página",
    lastAriaLabel: "Última página",
    lastTooltip: "Última página",
    labelDisplayedRows: "{from}-{to} de {count}",
  },
  toolbar: {
    searchTooltip: "Buscar",
    searchPlaceholder: "Buscar",
    nRowsSelected: "{0} productos seleccionados",
  },
};

function formatDate(value: string | null) {
  if (!value) return "—";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleDateString("es-CO");
}

export function durationTone(
  value: number | null | undefined
): "green" | "yellow" | "red" | null {
  if (value == null) return null;
  if (value <= 0) return "green";
  if (value <= 2) return "yellow";
  return "red";
}

const durationToneClass: Record<"green" | "yellow" | "red", string> = {
  green: styles.durationGreen,
  yellow: styles.durationYellow,
  red: styles.durationRed,
};

/* Footer dot tone per status (same keys as statusLabels) */
const statusDotClass: Record<DispatchStatus, string> = {
  order_preparation: styles.dotYellow,
  ready_in_warehouse: styles.dotGreen,
  dispatched: styles.dotPurple,
  product_picked: styles.dotSky,
  maritime_transit: styles.dotTeal,
  internal_warehouse_issue: styles.dotRed,
  partial_delivery: styles.dotOrange,
};

const methodBadgeClass: Record<DispatchMethod, string> = {
  pickup: styles.methodPickup,
  delivery: styles.methodDelivery,
  courier: styles.methodCourier,
};

export function DispatchesPage() {
  const {
    data: permissions = new Set<string>(),
    isLoading: permissionsLoading,
  } = useMyPermissions();
  const { data: currentUserId } = useMyUserId();

  const canViewAll = permissions.has("dispatches:view_all");
  const canViewOwn = permissions.has("dispatches:view_own");
  const canCreate = permissions.has("dispatches:create");
  const canEdit = permissions.has("dispatches:edit");
  const canApprove = permissions.has("dispatches:approve");
  const canReject = permissions.has("dispatches:reject");
  const canChangeStatus = permissions.has("dispatches:change_status");
  const canDelete = permissions.has("dispatches:delete");
  const canAddGuide = permissions.has("dispatches:add_guide");
  const canViewHistory = permissions.has("dispatches:view_history");

  const { data: dispatchesData, isLoading } = useGetDispatches();
  const { mutateAsync: createDispatch } = useCreateDispatch();
  const { mutateAsync: updateDispatch } = useUpdateDispatch();
  const { mutateAsync: approveDispatch } = useApproveDispatch();
  const { mutateAsync: rejectDispatch } = useRejectDispatch();
  const { mutateAsync: removeDispatch } = useDeleteDispatch();
  const { mutateAsync: addGuide } = useAddDispatchGuide();

  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [viewingDispatchId, setViewingDispatchId] = useState<number | null>(
    null
  );
  const [historyDispatchId, setHistoryDispatchId] = useState<number | null>(
    null
  );
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState<"all" | DispatchStatus>(
    "all"
  );
  const [editingDispatch, setEditingDispatch] = useState<Dispatch | null>(null);
  const [validationDialog, setValidationDialog] = useState<{
    mode: "approve" | "reject";
    row: Dispatch;
  } | null>(null);
  // The list endpoint omits items; load the detail (items) for the edit form.
  // Disabled while no row is being edited (id 0). On detail error the form
  // still opens with the row data and Guardar stays enabled (PUT is header-only).
  const { data: editingDetail, isLoading: isLoadingEditingDetail } =
    useGetDispatch(editingDispatch?.id ?? 0);
  const editingDetailMatches =
    !!editingDetail && editingDetail.dispatch?.id === editingDispatch?.id;
  const showEditLoading = isLoadingEditingDetail && !editingDetailMatches;

  const filteredDispatches = useMemo(() => {
    const rows = dispatchesData?.results ?? [];
    const byStatus =
      statusFilter === "all"
        ? rows
        : rows.filter((row) => row.status === statusFilter);
    const term = search.trim().toLowerCase();
    if (!term) return byStatus;
    return byStatus.filter((row) => {
      const quote = row.reservation?.quote_number || row.quote_number || "";
      const advisor =
        (row as any).created_by_name || String(row.created_by ?? "");
      return [row.recipient_name, quote, advisor]
        .filter(Boolean)
        .join(" ")
        .toLowerCase()
        .includes(term);
    });
  }, [dispatchesData, search, statusFilter]);

  if (!permissionsLoading && !canViewAll && !canViewOwn) {
    return (
      <div className={styles.noPermissions}>
        <p>No tienes permisos para ver despachos.</p>
      </div>
    );
  }

  const handleCreate = async (payload: CreateDispatchPayload) => {
    try {
      await createDispatch(payload);
      setIsCreateOpen(false);
      setEditingDispatch(null);
      successToast("Despacho creado exitosamente");
    } catch (err: any) {
      errorToast(err?.message || "Error al crear el despacho");
      throw err;
    }
  };

  const handleUpdate = async (id: number, payload: UpdateDispatchPayload) => {
    try {
      await updateDispatch({ id, payload });
      setEditingDispatch(null);
      successToast("Despacho actualizado exitosamente");
    } catch (err: any) {
      errorToast(err?.message || "Error al actualizar el despacho");
      throw err;
    }
  };

  const handleApprove = async (
    row: Dispatch,
    payload: ApproveDispatchPayload
  ) => {
    try {
      await approveDispatch({ id: row.id, payload });
      successToast("Despacho aprobado exitosamente");
    } catch (err: any) {
      errorToast(err?.message || "Error al aprobar el despacho");
      throw err;
    }
  };

  const handleReject = async (
    row: Dispatch,
    payload: RejectDispatchPayload
  ) => {
    try {
      await rejectDispatch({ id: row.id, payload });
      successToast("Despacho rechazado exitosamente");
    } catch (err: any) {
      errorToast(err?.message || "Error al rechazar el despacho");
      throw err;
    }
  };

  const handleValidationConfirm = async (result: DispatchValidationSubmit) => {
    if (!validationDialog) return;
    if (result.mode === "approve") {
      await handleApprove(validationDialog.row, {
        invoice_number: result.invoice_number,
        observation: result.observation,
      });
    } else {
      await handleReject(validationDialog.row, {
        observation: result.observation,
      });
    }
  };

  const handleDelete = async (row: Dispatch) => {
    try {
      await removeDispatch(row.id);
      successToast("Despacho eliminado exitosamente");
    } catch (err: any) {
      errorToast(err?.message || "Error al eliminar el despacho");
    }
  };

  const handleAddGuide = async (row: Dispatch) => {
    const guide = window.prompt("Ingrese la guía de envío");
    if (!guide || !guide.trim()) return;
    try {
      await addGuide({ id: row.id, tracking_guide: guide.trim() });
      successToast("Guía agregada exitosamente");
    } catch (err: any) {
      errorToast(err?.message || "Error al agregar la guía");
    }
  };

  return (
    <div className={styles.page}>
      <div className={styles.header}>
        <h2 className={styles.heading}>Registro de Despachos</h2>
        {canCreate && (
          <Button
            variant="contained"
            color="primary"
            startIcon={<AddIcon />}
            onClick={() => setIsCreateOpen(true)}
          >
            Nuevo Despacho
          </Button>
        )}
      </div>

      <div className={styles.filters} data-testid="dispatch-filters">
        <TextField
          placeholder="Buscar cliente, cotización, asesor..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          variant="outlined"
          size="small"
          className={styles.search}
        />
        <TextField
          select
          id="dispatch-status-filter"
          label="Estado"
          value={statusFilter}
          onChange={(e) =>
            setStatusFilter(e.target.value as "all" | DispatchStatus)
          }
          variant="outlined"
          size="small"
          InputLabelProps={{ shrink: true }}
          SelectProps={{
            native: true,
            inputProps: { id: "dispatch-status-filter" },
          }}
        >
          <option value="all">Todos</option>
          {(Object.keys(statusLabels) as DispatchStatus[]).map((status) => (
            <option key={status} value={status}>
              {statusLabels[status]}
            </option>
          ))}
        </TextField>
      </div>

      <DispatchTable
        data={filteredDispatches}
        totalCount={dispatchesData?.results.length ?? filteredDispatches.length}
        isLoading={isLoading}
        canEdit={canEdit}
        canApprove={canApprove}
        canReject={canReject}
        canDelete={canDelete}
        canAddGuide={canAddGuide}
        canViewHistory={canViewHistory}
        currentUserId={currentUserId}
        onApprove={(row) => setValidationDialog({ mode: "approve", row })}
        onReject={(row) => setValidationDialog({ mode: "reject", row })}
        onDelete={handleDelete}
        onAddGuide={handleAddGuide}
        onEdit={(row) => setEditingDispatch(row)}
        onView={(row) => setViewingDispatchId(row.id)}
        onHistory={(row) => setHistoryDispatchId(row.id)}
      />

      {isCreateOpen && (
        <DispatchForm
          open={isCreateOpen}
          onClose={() => setIsCreateOpen(false)}
          onCreate={handleCreate}
        />
      )}

      {editingDispatch !== null &&
        (showEditLoading ? (
          <Sheet open onOpenChange={() => setEditingDispatch(null)}>
            <SheetContent side="right">
              <div style={{ padding: 24 }} data-testid="dispatch-edit-loading">
                Cargando despacho…
              </div>
            </SheetContent>
          </Sheet>
        ) : (
          <DispatchForm
            key={editingDispatch.id}
            open={editingDispatch !== null}
            canChangeStatus={canChangeStatus}
            dispatch={{
              ...editingDispatch,
              items: editingDetailMatches
                ? editingDetail.items
                : editingDispatch.items,
              // locked_fields comes from the detail only (S9); list rows omit it.
              locked_fields: editingDetailMatches
                ? editingDetail.dispatch.locked_fields
                : editingDispatch.locked_fields,
              // items_editable comes from the detail only (S7); list rows omit it.
              items_editable: editingDetailMatches
                ? editingDetail.dispatch.items_editable
                : editingDispatch.items_editable,
            }}
            onClose={() => setEditingDispatch(null)}
            onUpdate={(payload) => handleUpdate(editingDispatch.id, payload)}
          />
        ))}

      {viewingDispatchId !== null && (
        <DispatchDetail
          dispatchId={viewingDispatchId}
          onClose={() => setViewingDispatchId(null)}
        />
      )}

      {historyDispatchId !== null && (
        <DispatchHistoryModal
          dispatchId={historyDispatchId}
          onClose={() => setHistoryDispatchId(null)}
        />
      )}

      {validationDialog !== null && (
        <DispatchValidationDialog
          open
          mode={validationDialog.mode}
          dispatch={validationDialog.row}
          onClose={() => setValidationDialog(null)}
          onConfirm={handleValidationConfirm}
        />
      )}
    </div>
  );
}

interface DispatchTableProps {
  data: Dispatch[];
  totalCount?: number;
  isLoading: boolean;
  canEdit: boolean;
  canApprove: boolean;
  canReject: boolean;
  canDelete: boolean;
  canAddGuide: boolean;
  canViewHistory: boolean;
  currentUserId?: number;
  onApprove: (row: Dispatch) => void;
  onReject: (row: Dispatch) => void;
  onDelete: (row: Dispatch) => void;
  onAddGuide: (row: Dispatch) => void;
  onEdit: (row: Dispatch) => void;
  onView: (row: Dispatch) => void;
  onHistory: (row: Dispatch) => void;
}

export function DispatchTable({
  data,
  totalCount,
  isLoading,
  canEdit,
  canApprove,
  canReject,
  canDelete,
  canAddGuide,
  canViewHistory,
  onApprove,
  onReject,
  onDelete,
  onAddGuide,
  onEdit,
  onView,
  onHistory,
}: DispatchTableProps) {
  const columns: Column<Dispatch>[] = useMemo(
    () => [
      {
        title: "",
        field: "actions",
        sorting: false,
        cellStyle: { whiteSpace: "nowrap", width: "1%" },
        render: (row) => {
          const items: RowActionItem[] = [
            {
              label: "Ver detalle",
              icon: <VisibilityIcon fontSize="small" />,
              onClick: () => onView(row),
            },
          ];

          if (canViewHistory) {
            items.push({
              label: "Ver historial",
              icon: <HistoryIcon fontSize="small" />,
              onClick: () => onHistory(row),
            });
          }

          if (canEdit && isEditableStatus(row.status)) {
            items.push({
              label: "Editar",
              icon: <EditIcon fontSize="small" />,
              onClick: () => onEdit(row),
            });
          }

          if (
            canApprove &&
            (row.validation_status === "pending" ||
              row.validation_status === "reserved")
          ) {
            items.push({
              label: "Aprobar",
              icon: <CheckCircleIcon fontSize="small" />,
              onClick: () => onApprove(row),
            });
          }

          if (
            canReject &&
            (row.validation_status === "pending" ||
              row.validation_status === "reserved")
          ) {
            items.push({
              label: "Rechazar",
              icon: <CancelIcon fontSize="small" />,
              onClick: () => onReject(row),
            });
          }

          if (
            canAddGuide &&
            (row.status === "order_preparation" ||
              row.status === "ready_in_warehouse")
          ) {
            items.push({
              label: "Agregar guía",
              icon: <LocalShippingIcon fontSize="small" />,
              onClick: () => onAddGuide(row),
            });
          }

          if (canDelete) {
            items.push({
              label: "Eliminar",
              icon: <DeleteIcon fontSize="small" />,
              onClick: () => onDelete(row),
              danger: true,
            });
          }

          return <RowActionsMenu items={items} />;
        },
      },
      {
        title: "Asesor",
        field: "created_by",
        render: (row) =>
          (row as any).created_by_name || String(row.created_by ?? "—"),
      },
      {
        title: "F. Solicitud",
        field: "created_at",
        render: (row) => formatDate(row.created_at),
      },
      {
        title: "Cotización",
        field: "quote_number",
        render: (row) => {
          const quote =
            row.quote_number ||
            row.reservation?.quote_number ||
            (row as any).quote_number;
          if (!quote) return <span className={styles.mutedDash}>—</span>;
          return <span className={styles.quotePill}>{quote}</span>;
        },
      },
      {
        title: "Estado",
        field: "status",
        render: (row) => (
          <span className={`${styles.badge} ${statusBadgeClass[row.status]}`}>
            {statusLabels[row.status] ?? row.status}
          </span>
        ),
      },
      {
        title: "Validación",
        field: "validation_status",
        render: (row) => (
          <span
            className={`${styles.badge} ${
              validationBadgeClass[row.validation_status]
            }`}
          >
            {validationLabels[row.validation_status] ?? row.validation_status}
          </span>
        ),
      },
      { title: "Cliente", field: "recipient_name" },
      {
        title: "Observación",
        field: "notes",
        render: (row) =>
          row.notes ? (
            <Tooltip title={row.notes}>
              <span
                className={styles.cellClamp}
                data-testid="dispatch-notes-cell"
              >
                {row.notes}
              </span>
            </Tooltip>
          ) : (
            "—"
          ),
      },
      {
        title: "Método de entrega",
        field: "method",
        render: (row) =>
          row.method ? (
            <span className={`${styles.badge} ${methodBadgeClass[row.method]}`}>
              {methodLabels[row.method] ?? row.method}
            </span>
          ) : (
            "—"
          ),
      },
      {
        title: "Ciudad / Dirección",
        field: "delivery_address",
        render: (row) =>
          row.delivery_address ? (
            <Tooltip title={row.delivery_address}>
              <span
                className={styles.cellClamp}
                data-testid="dispatch-address-cell"
              >
                {row.delivery_address}
              </span>
            </Tooltip>
          ) : (
            "—"
          ),
      },
      {
        title: "Guía",
        field: "tracking_guide",
        render: (row) => row.tracking_guide || "—",
      },
      {
        title: "F. Aprobación",
        field: "approved_at",
        render: (row) =>
          row.approved_at
            ? `${formatDate(row.approved_at)}${
                row.approved_by ? ` (${row.approved_by})` : ""
              }`
            : "—",
      },
      {
        title: "F. Despacho",
        field: "dispatch_date",
        render: (row) => formatDate(row.dispatch_date),
      },
      {
        title: "Duración",
        field: "duration_days",
        render: (row) => {
          const tone = durationTone(row.duration_days);
          if (tone == null) return "—";
          return (
            <span className={durationToneClass[tone]}>
              {String(row.duration_days)}
            </span>
          );
        },
      },
    ],
    [
      canEdit,
      canApprove,
      canReject,
      canDelete,
      canAddGuide,
      canViewHistory,
      onApprove,
      onReject,
      onDelete,
      onAddGuide,
      onEdit,
      onView,
      onHistory,
    ]
  );

  return (
    <div className={styles.tableCard}>
      <MaterialTable
        title=""
        columns={columns}
        data={data}
        isLoading={isLoading}
        options={{
          search: false,
          paging: true,
          pageSize: 10,
          pageSizeOptions: [10, 20, 50],
          padding: "dense",
          headerStyle: {
            backgroundColor: "#f9fafb",
            color: "#4b5563",
            fontSize: "12px",
            fontWeight: 600,
            textTransform: "uppercase",
            letterSpacing: "0.05em",
          },
          rowStyle: {},
        }}
        localization={materialTableES}
      />
      <div className={styles.tableFooter} data-testid="dispatch-footer">
        <div>
          Mostrando <strong>{data.length}</strong> de{" "}
          <strong>{totalCount ?? data.length}</strong> despachos
        </div>
        <div className={styles.footerCounts}>
          {(Object.keys(statusLabels) as DispatchStatus[]).map((status) => {
            const count = data.filter((row) => row.status === status).length;
            if (count === 0) return null;
            return (
              <span key={status}>
                <span className={`${styles.dot} ${statusDotClass[status]}`} />
                {count} {statusLabels[status]}
              </span>
            );
          })}
        </div>
      </div>
    </div>
  );
}

export default DispatchesPage;
